import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { SaveSimulatorEstimateInput } from 'used-vehicle-contracts';
import {
  DAMAGE_AMOUNTS,
  DAMAGE_GROUP_1_LABELS,
  DAMAGE_GROUP_2_LABELS,
  DEPRECIATION_CATEGORIES,
  type DepreciationCategory,
  defaultSimulatorValues,
  describeChange,
  diffSimulatorValues,
  englishPart,
  equityForBrand,
  GRADE_LABELS,
  type InspectionTable,
  leftAfter,
  optionByKey,
  outputsOf,
  SEVERITY_LABELS,
  SIMULATOR_ASSESSMENTS,
  SIMULATOR_BRANDS,
  SIMULATOR_GRADES,
  SIMULATOR_SEVERITIES,
  SIMULATOR_YEARS,
  simulate,
  type SimulatorAssessment,
  type SimulatorGrade,
  type SimulatorInputs,
  type SimulatorSeverity,
  type SimulatorValues,
  TRIM_LEVELS,
  VEHICLE_PRESETS,
  vehiclePreset,
} from 'used-vehicle-engine';
import { orpc } from '../../apis/orpc-query';
import { Field, NumberField, Seg, SelectField } from './controls';
import { money, numberOf, pct, pts, signed } from './format';
import { type ChartSeries, Legend, LineChart } from './LineChart';
import { applyPreset, initialInputs, withValueAt } from './simulator-state';
import { ValuesPanel } from './ValuesPanel';

const LRM = '‎';

/** The select value that opens a text field for a brand or a model not in the lists. */
const OTHER = '__other';

const CATEGORY_COLOR: Record<DepreciationCategory, string> = { low: 'var(--s-low)', medium: 'var(--s-medium)', high: 'var(--s-high)' };

const categoryName = (key: DepreciationCategory): string => DEPRECIATION_CATEGORIES.find((category) => category.key === key)?.name ?? key;

const gradeLabel = (grade: SimulatorGrade): string => `${GRADE_LABELS[grade].number} — ${GRADE_LABELS[grade].arabic}${LRM}`;

interface SwitchProps {
  label: string;
  name: string;
  value: boolean;
  /** The factor applied on Yes, as a positive fraction; `sign` says whether it adds or deducts. */
  factor: number;
  sign: '+' | '−';
  path: string;
  changed: boolean;
  seq: number;
  onChange: (value: boolean) => void;
  onFactor: (path: string, value: number) => void;
}

/** A yes/no switch with its factor editable right beside it, as the review asked; an edit is tracked. */
const Switch = ({ label, name, value, factor, sign, path, changed, seq, onChange, onFactor }: SwitchProps) => (
  <div className="field">
    <span className="lbl">
      {label}
      <small>{changed ? 'changed · tracked' : 'factor editable'}</small>
    </span>
    <div className={`switch${changed ? ' flagged' : ''}`}>
      <Seg
        name={name}
        value={value ? 'yes' : 'no'}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: `Yes · ${sign}${pts(factor)}%` },
        ]}
        onChange={(next) => onChange(next === 'yes')}
      />
      <label
        className="factor"
        title="The factor applied when the answer is Yes. Edit it here; the change is recorded on the saved estimate."
      >
        <input
          key={`${seq}-${path}`}
          type="number"
          min={0}
          max={90}
          step={1}
          defaultValue={pts(factor)}
          aria-label={`${label} factor percent`}
          onChange={(event) => onFactor(path, Math.max(0, numberOf(event.target.value)) / 100)}
        />
        <small>%</small>
      </label>
    </div>
  </div>
);

interface StepRow {
  step: string;
  detail: string;
  effect: string;
  after: string;
  result?: boolean;
}

/** The public depreciation simulator: a port of the one-file HTML version, plus saving for the export. */
export const SimulatorPage = () => {
  const queryClient = useQueryClient();
  const save = useMutation(orpc.simulator.save.mutationOptions());
  const [values, setValues] = useState<SimulatorValues>(defaultSimulatorValues);
  const [inputs, setInputs] = useState<SimulatorInputs>(initialInputs);
  /** Bumped when a preset writes new figures into the uncontrolled number fields. */
  const [fillSeq, setFillSeq] = useState(0);
  /** Bumped on "Reset to defaults" so every values cell remounts with its default. */
  const [valuesSeq, setValuesSeq] = useState(0);
  const [selected, setSelected] = useState<{ cat: string; val: string }>({ cat: '', val: '' });
  const [preowned, setPreowned] = useState('');
  const [remarks, setRemarks] = useState('');
  const [vin, setVin] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [dealOwner, setDealOwner] = useState('');
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const result = useMemo(() => simulate(values, inputs), [values, inputs]);
  const changes = useMemo(() => diffSimulatorValues(defaultSimulatorValues(), values), [values]);
  const changedPaths = useMemo(() => new Set(changes.map((change) => change.path)), [changes]);

  const set = <K extends keyof SimulatorInputs>(key: K, value: SimulatorInputs[K]): void => setInputs((previous) => ({ ...previous, [key]: value }));
  const setSeverity = (assessment: SimulatorAssessment, severity: SimulatorSeverity): void =>
    setInputs((previous) => ({ ...previous, sev: { ...previous.sev, [assessment]: severity } }));
  const setValueAt = (path: string, value: number | string): void => setValues((previous) => withValueAt(previous, path, value));
  const pick = (scope: 'cat' | 'val', key: string | null): void =>
    setSelected((previous) => ({ ...previous, [scope]: !key || previous[scope] === key ? '' : key }));

  const onPreset = (key: string): void => {
    const preset = vehiclePreset(key);

    if (preset) {
      setInputs((previous) => applyPreset(previous, preset));
      setFillSeq((sequence) => sequence + 1);
    }
  };

  const onBrand = (brand: string): void => {
    const equity = equityForBrand(brand);

    setInputs((previous) => ({ ...previous, brand, preset: null, equity: equity ?? previous.equity }));
  };

  const knownBrand = SIMULATOR_BRANDS.find((brand) => brand.name.toLowerCase() === inputs.brand.trim().toLowerCase());
  const brandChoice = knownBrand ? knownBrand.name : OTHER;
  const brandPresets = VEHICLE_PRESETS.filter((preset) => preset.brand.toLowerCase() === inputs.brand.trim().toLowerCase());
  const currentPreset = brandPresets.find((preset) => preset.key === inputs.preset && preset.model === inputs.model);
  const modelChoice = currentPreset ? currentPreset.key : OTHER;

  /** A listed brand sets its equity and, when it has presets, opens on the first one; "Other" clears the names. */
  const onBrandChoice = (choice: string): void => {
    if (choice === OTHER) {
      setInputs((previous) => ({ ...previous, brand: '', model: '', preset: null }));

      return;
    }

    const equity = equityForBrand(choice) ?? inputs.equity;
    const first = VEHICLE_PRESETS.find((preset) => preset.brand === choice);

    if (first) {
      setInputs((previous) => applyPreset({ ...previous, equity }, first));
      setFillSeq((sequence) => sequence + 1);
    } else {
      setInputs((previous) => ({ ...previous, brand: choice, equity, model: '', preset: null }));
    }
  };

  const onModelChoice = (choice: string): void => {
    if (choice === OTHER) {
      setInputs((previous) => ({ ...previous, model: '', preset: null }));

      return;
    }

    onPreset(choice);
  };

  const onReset = (): void => {
    setValues(defaultSimulatorValues());
    setValuesSeq((sequence) => sequence + 1);
    setFillSeq((sequence) => sequence + 1);
  };

  const onSave = async (): Promise<void> => {
    const brand = inputs.brand.trim();
    const model = inputs.model.trim();

    if (brand === '' || model === '' || vin.trim() === '' || customerName.trim() === '') {
      setStatus({ kind: 'err', text: 'Fill in the brand, model, VIN and customer name before saving.' });

      return;
    }

    const payload: SaveSimulatorEstimateInput = {
      inputs: { ...inputs, brand, model },
      values,
      changes,
      outputs: outputsOf(result),
      vin: vin.trim().toUpperCase(),
      customerName: customerName.trim(),
      dealOwner: dealOwner.trim() === '' ? null : dealOwner.trim(),
      preownedPrice: preowned.trim() === '' ? null : Math.max(0, numberOf(preowned)),
      remarks: remarks.trim() === '' ? null : remarks.trim(),
    };

    setStatus(null);

    try {
      const saved = await save.mutateAsync(payload);

      await queryClient.invalidateQueries({ queryKey: orpc.simulator.list.key() });
      setStatus({
        kind: 'ok',
        text: `Saved ${saved.inputs.brand} ${saved.inputs.model} at ${money(saved.outputs.simulatorPrice)} JOD. Open the saved estimates to review or export.`,
      });
      setPreowned('');
      setRemarks('');
      setVin('');
      setCustomerName('');
    } catch (error) {
      setStatus({ kind: 'err', text: error instanceof Error ? error.message : 'The estimate could not be saved.' });
    }
  };

  const { car, at, rows, byCategory } = result;
  const extraParts: string[] = [];
  const brandOption = optionByKey(values.brand, inputs.equity);
  const fuelOption = optionByKey(values.fuel, inputs.fuel);

  if (brandOption.pts) {
    extraParts.push(`${brandOption.name.toLowerCase()} brand equity ${signed(brandOption.pts)}`);
  }

  if (fuelOption.pts) {
    extraParts.push(`${fuelOption.name.toLowerCase()} ${signed(fuelOption.pts)}`);
  }

  const categorySeries: ChartSeries[] = DEPRECIATION_CATEGORIES.map(({ key, name }) => ({
    key,
    name: key === inputs.cat ? `${name} · selected` : name,
    color: CATEGORY_COLOR[key],
    width: key === inputs.cat ? 3 : 1.75,
    pts: values.cat[key].map((rate, index) => ({ n: index + 1, v: rate })),
  }));

  const valueSeries: ChartSeries[] = [
    {
      key: 'best',
      name: 'Best case',
      color: 'var(--s-best)',
      width: 2.25,
      markAt: car.age,
      pts: [{ n: 0, v: 1 }, ...rows.map((row) => ({ n: row.n, v: row.best.left }))],
    },
    {
      key: 'car',
      name: 'This car',
      color: 'var(--s-car)',
      width: 3,
      markAt: car.age,
      pts: [{ n: 0, v: 1 }, ...rows.map((row) => ({ n: row.n, v: row.car.left }))],
    },
    {
      key: 'worst',
      name: 'Worst case',
      color: 'var(--s-worst)',
      width: 2.25,
      dash: '7 4',
      markAt: car.age,
      pts: [{ n: 0, v: 1 }, ...rows.map((row) => ({ n: row.n, v: row.worst.left }))],
    },
  ];

  const worstService =
    optionByKey(values.service, values.worst.service).key === 'none' ? 'no service history' : `${values.worst.service} service history`;
  const stepRows = buildSteps(values, inputs, result.car, at.car, extraParts);

  const withPts = (option: { name: string; pts: number }): string => `${option.name}${option.pts ? ` · +${pts(option.pts)} pts` : ''}`;
  const withPct = (option: { name: string; pct: number }): string => `${option.name} · ${signed(option.pct)}%`;
  const severityOptions = SIMULATOR_SEVERITIES.map((severity) => ({
    value: severity,
    label: `${SEVERITY_LABELS[severity]} · ${pct(values.sev[severity], 1)}`,
  }));
  const damageOptions = (labels: readonly string[], amounts: readonly number[]) =>
    labels.map((label, index) => ({ value: String(index), label: `${label} · ${money(amounts[index] ?? 0)}` }));

  return (
    <>
      <section id="categories">
        <h2>Depreciation categories</h2>
        <div className="cat-block">
          <div className="cap">
            Depreciation taken in each year <small>% of the value left at the start of the year · edit a figure to try another ladder</small>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  {Array.from({ length: SIMULATOR_YEARS }, (_, index) => (
                    <th key={index}>Y{index + 1}</th>
                  ))}
                  <th>Left at Y7</th>
                </tr>
              </thead>
              <tbody>
                {DEPRECIATION_CATEGORIES.map(({ key, name }) => (
                  <tr
                    key={key}
                    className={key === inputs.cat ? 'sel' : undefined}
                  >
                    <td className="lbl">
                      <span
                        className="sw"
                        style={{ '--c': CATEGORY_COLOR[key] } as React.CSSProperties}
                      />
                      {name}
                      {key === inputs.cat && <small className="inline"> · selected</small>}
                    </td>
                    {values.cat[key].map((rate, index) => {
                      const path = `cat.${key}.${index}`;

                      return (
                        <td
                          key={path}
                          className={`n${changedPaths.has(path) ? ' changed' : ''}`}
                        >
                          <input
                            key={`${valuesSeq}-${path}`}
                            className="cell"
                            type="number"
                            min={0}
                            max={95}
                            step={1}
                            defaultValue={pts(rate)}
                            aria-label={`${name} year ${index + 1} percent`}
                            onChange={(event) => setValueAt(path, Math.max(0, Math.min(95, numberOf(event.target.value))) / 100)}
                          />
                        </td>
                      );
                    })}
                    <td className="n total">{pct(leftAfter(values.cat[key], SIMULATOR_YEARS))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <figure className="chart cat">
          <div className="cap">The same figures as lines</div>
          <LineChart
            series={categorySeries}
            x0={1}
            step={0.05}
            yMin={0}
            yMax={0.25}
            label="Depreciation taken in each year for the three categories"
            selected={selected.cat}
            onPick={(key) => pick('cat', key)}
            rows={(n) =>
              categorySeries.map((series) => ({
                label: series.name.replace(' · selected', ''),
                value: pct(series.pts.find((point) => point.n === n)?.v ?? 0, 1),
              }))
            }
          />
        </figure>
        <Legend
          items={categorySeries.map((series) => ({
            key: series.key,
            name: series.name.replace(' · selected', ''),
            color: series.color,
            width: series.width,
            note: series.key === inputs.cat ? 'selected' : undefined,
          }))}
          selected={selected.cat}
          onPick={(key) => pick('cat', key)}
        />
      </section>

      <section id="simulate">
        <h2>Simulate a vehicle</h2>
        <div className="sim">
          <form
            className="inputs"
            autoComplete="off"
            onSubmit={(event) => event.preventDefault()}
          >
            <div className="group">
              <h4>Brand and model equity</h4>
              <Field
                label="Brand"
                note="equity follows the brand"
              >
                <select
                  aria-label="Brand"
                  value={brandChoice}
                  onChange={(event) => onBrandChoice(event.target.value)}
                >
                  {values.brand.map((level) => (
                    <optgroup
                      key={level.key}
                      label={`${level.name} equity`}
                    >
                      {SIMULATOR_BRANDS.filter((brand) => brand.equity === level.key).map((brand) => (
                        <option
                          key={brand.name}
                          value={brand.name}
                        >
                          {brand.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  <optgroup label="Not listed">
                    <option value={OTHER}>Other brand…</option>
                  </optgroup>
                </select>
              </Field>
              {brandChoice === OTHER && (
                <Field label="Brand name">
                  <input
                    type="text"
                    value={inputs.brand}
                    placeholder="Type the brand"
                    maxLength={80}
                    onChange={(event) => onBrand(event.target.value)}
                  />
                </Field>
              )}
              <SelectField
                label="Brand equity"
                note="filled from a known brand"
                value={inputs.equity}
                options={values.brand.map((option) => ({ value: option.key, label: withPts(option) }))}
                onChange={(value) => set('equity', value as SimulatorInputs['equity'])}
              />
              {brandPresets.length > 0 && (
                <SelectField
                  label="Model"
                  note="a preset fills price and category"
                  value={modelChoice}
                  options={[
                    ...brandPresets.map((preset) => ({ value: preset.key, label: `${preset.model} · ${preset.grade}` })),
                    { value: OTHER, label: 'Other model…' },
                  ]}
                  onChange={onModelChoice}
                />
              )}
              {(brandPresets.length === 0 || modelChoice === OTHER) && (
                <Field label={brandPresets.length > 0 ? 'Model name' : 'Model'}>
                  <input
                    type="text"
                    value={inputs.model}
                    placeholder="Type the model"
                    maxLength={120}
                    onChange={(event) => setInputs((previous) => ({ ...previous, model: event.target.value, preset: null }))}
                  />
                </Field>
              )}
              <Field
                label="Depreciation category"
                block
              >
                <Seg
                  name="cat"
                  value={inputs.cat}
                  options={DEPRECIATION_CATEGORIES.map(({ key, name }) => ({ value: key, label: `${name} · ${pct(values.cat[key][0] ?? 0)}` }))}
                  onChange={(value) => set('cat', value as DepreciationCategory)}
                />
              </Field>
              <SelectField
                label="Agency reputation"
                value={inputs.agency}
                options={values.agency.map((option) => ({ value: option.key, label: withPct(option) }))}
                onChange={(value) => set('agency', value)}
              />
              <NumberField
                label="New price"
                note="JOD, last list price"
                value={inputs.price}
                fillKey={`price-${fillSeq}`}
                min={0}
                step={50}
                onChange={(value) => set('price', Math.max(0, value))}
              />
              <div className="pair">
                <SelectField
                  label="Powertrain"
                  value={inputs.fuel}
                  options={values.fuel.map((option) => ({ value: option.key, label: withPts(option) }))}
                  onChange={(value) => set('fuel', value)}
                />
                <SelectField
                  label="Trim level"
                  note="recorded only"
                  value={inputs.trim}
                  options={TRIM_LEVELS.map((trim) => ({ value: trim.key, label: trim.name }))}
                  onChange={(value) => set('trim', value)}
                />
              </div>
              <div className="pair">
                <SelectField
                  label="Demand for the model"
                  value={inputs.demand}
                  options={values.demand.map((option) => ({ value: option.key, label: withPct(option) }))}
                  onChange={(value) => set('demand', value)}
                />
                <SelectField
                  label="Market supply of the model"
                  value={inputs.supply}
                  options={values.supply.map((option) => ({ value: option.key, label: withPct(option) }))}
                  onChange={(value) => set('supply', value)}
                />
              </div>
            </div>

            <div className="group">
              <h4>General vehicle description and condition</h4>
              <div className="pair">
                <SelectField
                  label="Age"
                  note="years"
                  value={String(inputs.age)}
                  options={Array.from({ length: SIMULATOR_YEARS }, (_, index) => ({ value: String(index + 1), label: String(index + 1) }))}
                  onChange={(value) => set('age', parseInt(value, 10) || 1)}
                />
                <NumberField
                  label="Odometer"
                  note="km"
                  value={inputs.odometer}
                  fillKey={`odometer-${fillSeq}`}
                  min={0}
                  step={1000}
                  onChange={(value) => set('odometer', Math.max(0, value))}
                />
              </div>
              <div className="pair">
                <NumberField
                  label="Owners"
                  value={inputs.owners}
                  fillKey={`owners-${fillSeq}`}
                  min={0}
                  max={30}
                  step={1}
                  onChange={(value) => set('owners', Math.max(0, Math.round(value)))}
                />
                <SelectField
                  label="Usage"
                  value={inputs.usage}
                  options={values.usage.map((option) => ({ value: option.key, label: withPct(option) }))}
                  onChange={(value) => set('usage', value)}
                />
              </div>
              <SelectField
                label="Regional specs"
                value={inputs.specs}
                options={values.specs.map((option) => ({ value: option.key, label: withPct(option) }))}
                onChange={(value) => set('specs', value)}
              />
              <Switch
                label="Warranty available?"
                name="warranty"
                value={inputs.warranty}
                factor={values.warranty}
                sign="+"
                path="warranty"
                changed={changedPaths.has('warranty')}
                seq={valuesSeq}
                onChange={(value) => set('warranty', value)}
                onFactor={setValueAt}
              />
              <Switch
                label="Model was facelifted?"
                name="facelift"
                value={inputs.facelift}
                factor={values.facelift}
                sign="−"
                path="facelift"
                changed={changedPaths.has('facelift')}
                seq={valuesSeq}
                onChange={(value) => set('facelift', value)}
                onFactor={setValueAt}
              />
              <Switch
                label="New customs factor"
                name="customs"
                value={inputs.customs}
                factor={values.customs}
                sign="−"
                path="customs"
                changed={changedPaths.has('customs')}
                seq={valuesSeq}
                onChange={(value) => set('customs', value)}
                onFactor={setValueAt}
              />
            </div>

            <div className="group">
              <h4>Vehicle specific condition</h4>
              <SelectField
                label="Chassis · سبعة جيد"
                value={inputs.grade}
                options={SIMULATOR_GRADES.map((grade) => ({
                  value: grade,
                  label: `${gradeLabel(grade)} · ${pct(values.grade[inputs.table][grade], 1)}`,
                }))}
                onChange={(value) => set('grade', value as SimulatorGrade)}
              />
              <div className="pair">
                <SelectField
                  label="Interior"
                  value={inputs.sev.interior}
                  options={severityOptions}
                  onChange={(value) => setSeverity('interior', value as SimulatorSeverity)}
                />
                <SelectField
                  label="Body and exterior"
                  value={inputs.sev.body}
                  options={severityOptions}
                  onChange={(value) => setSeverity('body', value as SimulatorSeverity)}
                />
                <SelectField
                  label="Engine"
                  value={inputs.sev.engine}
                  options={severityOptions}
                  onChange={(value) => setSeverity('engine', value as SimulatorSeverity)}
                />
                <SelectField
                  label="Hybrid / EV system"
                  value={inputs.sev.hybrid}
                  options={severityOptions}
                  disabled={!car.hv}
                  onChange={(value) => setSeverity('hybrid', value as SimulatorSeverity)}
                />
              </div>
              <SelectField
                label="Tyres"
                value={inputs.sev.tires}
                options={severityOptions}
                onChange={(value) => setSeverity('tires', value as SimulatorSeverity)}
              />
              <SelectField
                label="Damage group 1"
                note="fixed JOD"
                value={String(inputs.dmg1)}
                options={damageOptions(DAMAGE_GROUP_1_LABELS, DAMAGE_AMOUNTS[inputs.table].g1)}
                onChange={(value) => set('dmg1', parseInt(value, 10) || 0)}
              />
              <SelectField
                label="Damage group 2"
                note="fixed JOD"
                value={String(inputs.dmg2)}
                options={damageOptions(DAMAGE_GROUP_2_LABELS, DAMAGE_AMOUNTS[inputs.table].g2)}
                onChange={(value) => set('dmg2', parseInt(value, 10) || 0)}
              />
              <SelectField
                label="Service history"
                value={inputs.service}
                options={values.service.map((option) => ({ value: option.key, label: withPct(option) }))}
                onChange={(value) => set('service', value)}
              />
            </div>
          </form>

          <div className="outputs">
            <div className="readout">
              {[
                { color: 'var(--s-best)', name: 'Best case', value: at.best, desc: 'A clean car, lightly driven, full service history' },
                { color: 'var(--s-car)', name: 'This car', value: at.car, desc: 'The car as you entered it' },
                { color: 'var(--s-worst)', name: 'Worst case', value: at.worst, desc: 'Not clean, high mileage, no maintenance' },
              ].map((card) => (
                <div
                  key={card.name}
                  className="card"
                  style={{ '--c': card.color } as React.CSSProperties}
                >
                  <h4>
                    {card.name} <span className="q">· Year {car.age}</span>
                  </h4>
                  <div className="fig">
                    {money(card.value.appraisal)}
                    <small>JOD · {pct(card.value.left)} of new</small>
                  </div>
                  <div className="desc">{card.desc}</div>
                </div>
              ))}
            </div>

            <div className="save">
              <h4>
                Save this estimate <span className="q">· one row of the review sheet</span>
              </h4>
              <div className="row deal">
                <Field label="VIN">
                  <input
                    type="text"
                    maxLength={32}
                    value={vin}
                    placeholder="chassis number"
                    autoCapitalize="characters"
                    onChange={(event) => setVin(event.target.value)}
                  />
                </Field>
                <Field label="Customer name">
                  <input
                    type="text"
                    maxLength={120}
                    value={customerName}
                    placeholder="who the car belongs to"
                    onChange={(event) => setCustomerName(event.target.value)}
                  />
                </Field>
                <Field
                  label="Deal owner"
                  note="optional"
                >
                  <input
                    type="text"
                    maxLength={120}
                    value={dealOwner}
                    placeholder="salesperson"
                    onChange={(event) => setDealOwner(event.target.value)}
                  />
                </Field>
              </div>
              <div className="row">
                <Field
                  label="Preowned price"
                  note="JOD, observed"
                >
                  <input
                    type="number"
                    min={0}
                    step={50}
                    value={preowned}
                    placeholder="optional"
                    onChange={(event) => setPreowned(event.target.value)}
                  />
                </Field>
                <Field label="Remarks">
                  <input
                    type="text"
                    maxLength={2000}
                    value={remarks}
                    placeholder="optional"
                    onChange={(event) => setRemarks(event.target.value)}
                  />
                </Field>
                <button
                  type="button"
                  className="plain primary"
                  disabled={save.isPending}
                  onClick={() => void onSave()}
                >
                  {save.isPending ? 'Saving…' : 'Save this estimate'}
                </button>
              </div>
              {status && <span className={`notice ${status.kind}`}>{status.text}</span>}
              {changes.length > 0 && (
                <div className="changes">
                  <b>
                    {changes.length} {changes.length === 1 ? 'value differs' : 'values differ'} from the defaults and will be flagged on this
                    estimate:
                  </b>
                  <ul>
                    {changes.map((change) => (
                      <li key={change.path}>{describeChange(change)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="compare">
              <h3>
                This car on each depreciation category <span>Year {car.age} · select a row to use that category</span>
              </h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Year 1</th>
                      <th>Best case</th>
                      <th>This car</th>
                      <th>Worst case</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCategory.map((comparison) => (
                      <tr
                        key={comparison.cat}
                        className={comparison.cat === inputs.cat ? 'sel' : undefined}
                        title={`Use the ${categoryName(comparison.cat)} category`}
                        onClick={() => set('cat', comparison.cat)}
                      >
                        <td className="lbl">
                          <span
                            className="sw"
                            style={{ '--c': CATEGORY_COLOR[comparison.cat] } as React.CSSProperties}
                          />
                          {categoryName(comparison.cat)}
                          {comparison.cat === inputs.cat && <small className="inline"> · selected</small>}
                        </td>
                        <td className="n">{pct(values.cat[comparison.cat][0] ?? 0)}</td>
                        <td className="n">
                          {money(comparison.best.appraisal)}
                          <small>{pct(comparison.best.left)}</small>
                        </td>
                        <td className="n car">
                          {money(comparison.car.appraisal)}
                          <small>{pct(comparison.car.left)}</small>
                        </td>
                        <td className="n">
                          {money(comparison.worst.appraisal)}
                          <small>{pct(comparison.worst.left)}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {at.car.blocked && (
                <p className="table-note">The fixed damage exceeds the car’s value at this age; the tool would block this valuation.</p>
              )}
            </div>

            <figure className="chart">
              <div className="cap">
                Value left after each year <small>% of the new price · marks show the car’s age</small>
              </div>
              <LineChart
                series={valueSeries}
                x0={0}
                step={0.1}
                yMin={1}
                yMax={1}
                label="Value left after each year for the best case, this car and the worst case"
                selected={selected.val}
                onPick={(key) => pick('val', key)}
                rows={(n) => {
                  if (n === 0) {
                    return valueSeries.map((series) => ({ label: series.name, value: '100%', note: money(inputs.price) }));
                  }

                  const row = rows[n - 1]!;

                  return [
                    { label: 'Best case', value: pct(row.best.left), note: money(row.best.appraisal) },
                    { label: 'This car', value: pct(row.car.left), note: money(row.car.appraisal) },
                    { label: 'Worst case', value: pct(row.worst.left), note: money(row.worst.appraisal) },
                  ];
                }}
              />
            </figure>
            <Legend
              items={[
                {
                  key: 'best',
                  name: 'Best case',
                  color: 'var(--s-best)',
                  width: 2.25,
                  note: `7 جيد${LRM}, Excellent, full service, within the mileage allowance`,
                },
                { key: 'car', name: 'This car', color: 'var(--s-car)', width: 3, note: 'your entries' },
                {
                  key: 'worst',
                  name: 'Worst case',
                  color: 'var(--s-worst)',
                  width: 2.25,
                  dash: '7 4',
                  note: `${GRADE_LABELS[values.worst.grade].number} جيد${LRM}, ${SEVERITY_LABELS[values.worst.sev]}, ${worstService}, ${money(values.worst.kmPerYear)} km a year`,
                },
              ]}
              selected={selected.val}
              onPick={(key) => pick('val', key)}
            />

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Best case</th>
                    <th>%</th>
                    <th>This car</th>
                    <th>%</th>
                    <th>Worst case</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.n}
                      className={row.n === car.age ? 'age' : undefined}
                    >
                      <td className="lbl">
                        Year {row.n}
                        {row.n === car.age && <small className="inline"> · this car</small>}
                      </td>
                      <td className="n">{money(row.best.appraisal)}</td>
                      <td className="n">{pct(row.best.left)}</td>
                      <td className="n">{money(row.car.appraisal)}</td>
                      <td className="n">{pct(row.car.left)}</td>
                      <td className="n">{money(row.worst.appraisal)}</td>
                      <td className="n">{pct(row.worst.left)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3>
              Where the value goes <span>this car at Year {car.age}</span>
            </h3>
            <div className="table-wrap">
              <table className="steps">
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Effect</th>
                    <th>Value after</th>
                  </tr>
                </thead>
                <tbody>
                  {stepRows.map((row) => (
                    <tr
                      key={row.step}
                      className={row.result ? 'result' : undefined}
                    >
                      <td className="lbl">
                        {row.step}
                        <small>{row.detail}</small>
                      </td>
                      <td className="n eff">{row.effect}</td>
                      <td className="n">{row.after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section id="values">
        <ValuesPanel
          values={values}
          changedPaths={changedPaths}
          seq={valuesSeq}
          table={inputs.table}
          onChange={setValueAt}
          onTableChange={(table: InspectionTable) => set('table', table)}
          onReset={onReset}
        />
      </section>
    </>
  );
};

/** The "where the value goes" breakdown, one row per step of the arithmetic, as the HTML version printed it. */
const buildSteps = (
  values: SimulatorValues,
  inputs: SimulatorInputs,
  car: ReturnType<typeof simulate>['car'],
  r: ReturnType<typeof simulate>['at']['car'],
  extraParts: string[],
): StepRow[] => {
  const steps: StepRow[] = [];
  const ladderText = car.ladder
    .slice(0, car.age)
    .map((rate) => `${pts(rate)}%`)
    .join(', ');
  const preset = vehiclePreset(inputs.preset);

  steps.push({
    step: 'New price',
    detail: preset ? `${inputs.brand} ${inputs.model}, ${preset.grade}` : `${inputs.brand} ${inputs.model}`,
    effect: '',
    after: money(inputs.price),
  });
  steps.push({
    step: 'Age depreciation',
    detail: `${categoryName(inputs.cat)} category${extraParts.length ? ` plus ${extraParts.join(', ')} points spread over Years 1 to 3` : ''}: ${ladderText}, compounded`,
    effect: `−${pct(r.dep, 1)}`,
    after: money(inputs.price * r.depLeft),
  });
  steps.push({
    step: 'Mileage',
    detail: `${money(r.odometer)} km against an allowance of ${money(r.expectedKm)} km${
      r.steps
        ? `: ${money(r.excess)} km over, ${r.steps} ${r.steps === 1 ? 'started step' : 'started steps'} of ${money(values.km.step)} km`
        : ': within the allowance'
    }`,
    effect: r.mileage ? `−${pct(r.mileage, 1)}` : '0%',
    after: money(inputs.price * r.depLeft * (1 - r.mileage)),
  });
  steps.push({
    step: 'Owners',
    detail: `${car.owners} ${car.owners === 1 ? 'owner' : 'owners'}, ${values.owners.allowance} allowed`,
    effect: r.owner ? `−${pct(r.owner, 1)}` : '0%',
    after: money(r.base),
  });

  const inspectionText = [`Chassis ${GRADE_LABELS[inputs.grade].number} جيد${LRM} ${pct(r.gradeRate, 1)}`]
    .concat(
      SIMULATOR_ASSESSMENTS.map((assessment) =>
        assessment.key === 'hybrid' && !car.hv ? `${assessment.name} not applicable` : `${assessment.name} ${pct(r.sevRates[assessment.key], 1)}`,
      ),
    )
    .join(', ');

  steps.push({
    step: 'Inspection',
    detail: `${inspectionText} (${inputs.table} table)`,
    effect: r.inspection ? `−${pct(r.inspection, 1)}` : '0%',
    after: money(r.base * (1 - r.inspection)),
  });
  steps.push({
    step: 'Fixed damage',
    detail: `${englishPart(DAMAGE_GROUP_1_LABELS[inputs.dmg1] ?? DAMAGE_GROUP_1_LABELS[0]!)} and ${englishPart(DAMAGE_GROUP_2_LABELS[inputs.dmg2] ?? DAMAGE_GROUP_2_LABELS[0]!).toLowerCase()} (${inputs.table} table)`,
    effect: r.fixed ? `−${money(r.fixed)} JOD` : '0 JOD',
    after: money(Math.max(0, r.inspected)),
  });

  if (r.serviceRate) {
    steps.push({
      step: 'Service history',
      detail: inputs.service === 'none' ? 'no service history' : `${inputs.service} service history`,
      effect: `${signed(r.serviceRate)}%`,
      after: money(Math.max(0, r.afterService)),
    });
  }

  if (r.usageRate) {
    steps.push({
      step: 'Usage',
      detail: optionByKey(values.usage, inputs.usage).name,
      effect: `${signed(r.usageRate)}%`,
      after: money(Math.max(0, r.afterUsage)),
    });
  }

  if (inputs.facelift) {
    steps.push({
      step: 'Facelift',
      detail: 'the model was facelifted',
      effect: `−${pct(r.faceliftRate, 1)}`,
      after: money(Math.max(0, r.afterFace)),
    });
  }

  if (inputs.customs) {
    steps.push({
      step: 'Customs market impact',
      detail: 'a new customs factor applies',
      effect: `−${pct(r.customsRate, 1)}`,
      after: money(Math.max(0, r.afterCustoms)),
    });
  }

  if (r.specsRate) {
    steps.push({
      step: 'Regional specs',
      detail: optionByKey(values.specs, inputs.specs).name,
      effect: `${signed(r.specsRate)}%`,
      after: money(Math.max(0, r.afterSpecs)),
    });
  }

  if (r.warrantyRate) {
    steps.push({ step: 'Warranty', detail: 'warranty available', effect: `${signed(r.warrantyRate)}%`, after: money(Math.max(0, r.afterWarranty)) });
  }

  if (r.agencyRate) {
    steps.push({
      step: 'Agency reputation',
      detail: optionByKey(values.agency, inputs.agency).name.toLowerCase(),
      effect: `${signed(r.agencyRate)}%`,
      after: money(Math.max(0, r.afterAgency)),
    });
  }

  if (r.marketRate) {
    steps.push({
      step: 'Market',
      detail: `demand ${optionByKey(values.demand, inputs.demand).name.toLowerCase()}, supply ${optionByKey(values.supply, inputs.supply).name.toLowerCase()}`,
      effect: `${signed(r.marketRate)}%`,
      after: money(Math.max(0, r.afterMarket)),
    });
  }

  steps.push({
    step: 'Rounded appraisal',
    detail: `nearest ${money(values.rounding)} JOD`,
    effect: `${pct(r.left)} of new`,
    after: money(r.appraisal),
    result: true,
  });

  return steps;
};
